/**
 * DORI iso-density curves on ground plane (Phase 4 — 3D-aware refactor).
 *
 * Per EN 62676-4 / IEC 62676-4:2025 the DORI thresholds (Detect 25, Observe
 * 62.5, Recognize 125, Identify 250 px/m) are typically defined in terms of
 * SLANT distance from camera to subject (the line-of-sight distance). For
 * a level (tilt=0) camera at height h, the corresponding ground-plane
 * iso-density radius is:
 *
 *   ground_radius = √(slant_distance² − h²)
 *
 * This is the same conversion the coverage cone's far edge uses — see
 * `groundReachM` in `./frustum`, which is the single definition of what a
 * range figure means. The two used to disagree: the bands corrected for `h`
 * and the cone did not, so one number produced two radii on one drawing.
 *
 * The legacy renderer ignored `h` and rendered the iso-curve as a circle
 * of radius `slant_distance`. For high-mounted cameras with short DORI
 * ranges the difference is significant: a 6 m camera with Identify at 8 m
 * slant should show a ground circle of √(64−36) = 5.3 m, NOT 8 m. The old
 * code overestimated by 33% on this case.
 *
 * For tilted cameras, off-axis foreshortening adds further correction.
 * Phase 4 v1 (this module) handles ground-projection of slant-distance
 * iso-spheres correctly. Foreshortening per-angle correction is deferred
 * to a Phase 4 v2 follow-up — current accuracy is already much better
 * than the legacy 2D-only approach.
 *
 * The functions here are pure and testable. Renderer calls them and
 * passes the returned polyline / radius into the SVG layer.
 */

import type { CameraFrustum } from './frustum';
import type { Point2D } from './projection';

/** Result of slant-to-ground-radius conversion for one DORI tier.
 *  When `tierUnreachable` is true the camera mounting is so high that the
 *  iso-density slant sphere doesn't intersect the ground at all
 *  (slant ≤ h). The renderer should hide the iso-curve in that case and
 *  surface a UI hint that the DORI tier is unreachable on the ground.
 */
export interface IsoCurveCircle {
  groundRadiusM: number;
  groundRadiusPx: number;
  tierUnreachable: boolean;
}

/** Convert a slant-distance threshold (DORI maxDistance from doriUtils) to
 *  the ground-plane radius where pixel density equals the threshold.
 *
 *  Returns `tierUnreachable: true` when ANY of:
 *    a) The slant sphere is entirely below the camera (slant ≤ h) — impossible
 *       to see, camera mounted above the slant sphere's reach.
 *    b) The required ground radius is BEYOND where the camera's upper FOV
 *       edge lands on the ground given its tilt — i.e. the camera physically
 *       can't see ground that far away regardless of its optical range. Without
 *       this guard a narrow-FOV / shallow-tilt camera would show "Overview 124m"
 *       labels even though its actual ground footprint stops at, say, 22 m.
 *    c) The required ground radius is CLOSER than where the camera's lower FOV
 *       edge lands on the ground — high-mount + steep-tilt configurations have
 *       a near-edge cutoff (lower frustum plane hits ground at h / tan(tilt +
 *       vfov/2)). Without this guard a high-mounted 12 m camera shows
 *       "Recognize 16 m" floating above the cone even though the entire
 *       16 m ring lies inside the unviewable near zone.
 */
export function slantToGroundRadius(
  frustum: CameraFrustum,
  slantDistM: number,
  /**
   * Optional: elevation of the target ground point MINUS elevation of the
   * camera's mounting base, in metres. Positive = target is higher than
   * camera base (uphill). Negative = lower (downhill).
   *
   * The effective vertical drop the slant sphere has to cover becomes
   * (frustum.h - terrainDeltaM):
   *   - downhill target (delta < 0): drop grows, ground radius shrinks
   *   - uphill target (delta > 0):   drop shrinks, ground radius grows
   *
   * Default 0 = flat terrain (full backward compat with pre-WP-5 callers).
   */
  terrainDeltaM = 0,
): IsoCurveCircle {
  const effectiveDrop = frustum.h - terrainDeltaM;
  // |drop| ≥ slant → no real ground intersection (target is "above" the slant
  // sphere's reach in absolute Δz). Same physics as the pre-terrain check.
  if (Math.abs(effectiveDrop) >= slantDistM) {
    return { groundRadiusM: 0, groundRadiusPx: 0, tierUnreachable: true };
  }
  const groundRadiusM = Math.sqrt(slantDistM * slantDistM - effectiveDrop * effectiveDrop);

  // FOV-aware reachability gate. Upper edge of the FOV at (tilt - vfov/2)
  // below horizon. If this angle is ≥ 0 (i.e. upper edge above horizon) the
  // camera can see arbitrarily far on the ground — only renderRange caps it.
  // If upper edge is below horizon, the camera can see ground up to
  // h / tan(angle_below_horizon).
  const upperEdgeBelowHorizonRad = (frustum.tiltDeg - frustum.vfovDeg / 2) * Math.PI / 180;
  if (upperEdgeBelowHorizonRad > 0) {
    const maxReachableGroundM = frustum.h / Math.tan(upperEdgeBelowHorizonRad);
    if (groundRadiusM > maxReachableGroundM) {
      return { groundRadiusM, groundRadiusPx: groundRadiusM * frustum.pixelsPerMeter, tierUnreachable: true };
    }
  }

  // Near-edge gate (mirror of the upper-edge case). Lower frustum edge is at
  // (tilt + vfov/2) below horizon; for a downward-pointing camera this angle
  // is always positive, so a finite near-edge ground distance always exists
  // unless the camera looks straight down (lowerEdge ≈ 90°, tan → ∞, minRadius
  // → 0). Skip when the angle clips past 90° (camera pointed up past nadir,
  // physically nonsensical here).
  const lowerEdgeBelowHorizonRad = (frustum.tiltDeg + frustum.vfovDeg / 2) * Math.PI / 180;
  if (lowerEdgeBelowHorizonRad > 0 && lowerEdgeBelowHorizonRad < Math.PI / 2) {
    const minReachableGroundM = frustum.h / Math.tan(lowerEdgeBelowHorizonRad);
    if (groundRadiusM < minReachableGroundM) {
      return { groundRadiusM, groundRadiusPx: groundRadiusM * frustum.pixelsPerMeter, tierUnreachable: true };
    }
  }

  return {
    groundRadiusM,
    groundRadiusPx: groundRadiusM * frustum.pixelsPerMeter,
    tierUnreachable: false,
  };
}

/** Generate a closed polyline for a circle of radius `r` centered at
 *  (cx, cy). Used when rendering the iso-curve as a clipped polygon
 *  inside the camera's FOV cone. Default 64 samples gives smooth curve
 *  at typical zoom levels without overhead.
 */
export function generateCirclePolyline(
  cx: number,
  cy: number,
  r: number,
  samples = 64,
): Point2D[] {
  if (r <= 0) return [];
  const out: Point2D[] = new Array(samples);
  for (let i = 0; i < samples; i++) {
    const theta = (i / samples) * 2 * Math.PI;
    out[i] = {
      x: cx + r * Math.cos(theta),
      y: cy + r * Math.sin(theta),
    };
  }
  return out;
}

/** Compute the ground-plane iso-density polyline for a single DORI tier.
 *
 *  Returns null when the tier is unreachable (camera too high vs. slant
 *  threshold). Otherwise returns a closed polyline ready to be passed
 *  through polygon Boolean ops to clip against the FOV cone.
 *
 *  This is the canonical 3D-aware DORI math — single source of truth for
 *  the live designer, DXF export, PDF export, and elevation-view tools.
 */
export function computeDoriIsoCurve(
  frustum: CameraFrustum,
  slantDistM: number,
  samples = 64,
): Point2D[] | null {
  const { groundRadiusPx, tierUnreachable } = slantToGroundRadius(frustum, slantDistM);
  if (tierUnreachable) return null;
  return generateCirclePolyline(frustum.x, frustum.y, groundRadiusPx, samples);
}

/** Terrain-aware iso-curve sampler.
 *
 *  Given a sampler `terrainDeltaAt(angleRad, approxRadiusM) -> deltaM`, this
 *  walks N azimuths around the camera, asks the caller for the local terrain
 *  drop at that azimuth (relative to the camera base), and projects the slant
 *  threshold onto that local ground point. The resulting polygon is no longer
 *  a circle — it shortens on uphill bearings (smaller ground radius) and
 *  lengthens on downhill bearings, matching real-world surveillance physics.
 *
 *  Returns null if EVERY sampled azimuth is unreachable. Returns a partial
 *  polyline (with possibly-collapsed vertices) when some azimuths reach and
 *  others don't — caller can decide whether to clip to FOV or hide.
 *
 *  WP-5: math layer only. Renderer integration is a follow-up (CameraRenderer
 *  still passes flat terrain delta = 0).
 */
export function computeDoriIsoCurveOnTerrain(
  frustum: CameraFrustum,
  slantDistM: number,
  terrainDeltaAt: (azimuthRad: number, approxRadiusM: number) => number,
  samples = 64,
): Point2D[] | null {
  const out: Point2D[] = [];
  let anyReached = false;
  for (let i = 0; i < samples; i++) {
    const theta = (i / samples) * 2 * Math.PI;
    // Two-pass: first guess uses flat-ground radius, then refine with the
    // sampled terrain at that radius. Two iterations is enough for smooth
    // terrain (sub-1% radius error in practice).
    let r1 = slantToGroundRadius(frustum, slantDistM, 0).groundRadiusM;
    const delta1 = terrainDeltaAt(theta, r1);
    const ring2 = slantToGroundRadius(frustum, slantDistM, delta1);
    if (ring2.tierUnreachable) {
      // Anchor to camera center for that azimuth (degenerate vertex) so the
      // polyline stays closed. Renderer can decide to discard if all collapse.
      out.push({ x: frustum.x, y: frustum.y });
      continue;
    }
    anyReached = true;
    out.push({
      x: frustum.x + ring2.groundRadiusPx * Math.cos(theta),
      y: frustum.y + ring2.groundRadiusPx * Math.sin(theta),
    });
  }
  return anyReached ? out : null;
}

/** Per-tier iso-curve metadata: the polyline (or null if unreachable),
 *  plus the effective ground radius for legacy renderers that consume
 *  a single distance value.
 */
export interface DoriTierIso {
  type: 'detect' | 'observe' | 'recognize' | 'identify';
  slantDistM: number;
  groundRadiusM: number;
  groundRadiusPx: number;
  tierUnreachable: boolean;
  polyline: Point2D[] | null;
}

/** Compute iso-curves for all four standard DORI tiers given their slant
 *  distances. Caller obtains slant distances from `doriUtils.calculateDORIZones`
 *  (which uses focal length / sensor width / pixel resolution per EN 62676-4)
 *  and passes them here for ground-plane projection.
 */
export function computeAllDoriIsoCurves(
  frustum: CameraFrustum,
  zones: { type: 'detect' | 'observe' | 'recognize' | 'identify'; maxDistance: number }[],
  samples = 64,
): DoriTierIso[] {
  return zones.map(z => {
    const c = slantToGroundRadius(frustum, z.maxDistance);
    return {
      type: z.type,
      slantDistM: z.maxDistance,
      groundRadiusM: c.groundRadiusM,
      groundRadiusPx: c.groundRadiusPx,
      tierUnreachable: c.tierUnreachable,
      polyline: c.tierUnreachable
        ? null
        : generateCirclePolyline(frustum.x, frustum.y, c.groundRadiusPx, samples),
    };
  });
}
